import { useEffect, useMemo, useState } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { firestore } from '../database/firebase';
import { emailDomain, isEmail } from '../utils/email';

const toOrgId = (org = '') =>
  (org || 'org')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export default function useTeam({ user }) {
  const orgId = useMemo(() => toOrgId(user?.organization || 'org'), [user]);
  const userDomain = useMemo(() => emailDomain(user?.email || ''), [user]);
  const ref = useMemo(() => doc(firestore, 'organizations', orgId), [orgId]);

  const [team, setTeam] = useState({
    organization: user?.organization || '',
    ownerId: user?.uid || '',
    ownerEmail: user?.email || '',
    allowedDomains: [userDomain],
    allowAnyDomain: false,
    members: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let unsub = () => {};
    (async () => {
      try {
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          await setDoc(ref, {
            organization: user?.organization || 'Organization',
            ownerId: user?.uid || '',
            ownerEmail: user?.email || '',
            allowedDomains: [userDomain],
            allowAnyDomain: false,
            members: [],
          });
        }
        unsub = onSnapshot(ref, ds => {
          const data = ds.data() || {};
          setTeam({
            organization: data.organization || user?.organization || '',
            ownerId: data.ownerId || user?.uid || '',
            ownerEmail: data.ownerEmail || user?.email || '',
            allowedDomains: Array.isArray(data.allowedDomains) ? data.allowedDomains : [userDomain],
            allowAnyDomain: data.allowAnyDomain || false,
            members: Array.isArray(data.members) ? data.members : [],
          });
          setLoading(false);
        });
      } catch (e) {
        setError(e?.message || 'Failed to load team');
        setLoading(false);
      }
    })();
    return () => unsub && unsub();
  }, [ref, userDomain, user?.organization, user?.uid, user?.email]);

  const addMember = async rawEmail => {
    const e = (rawEmail || '').trim().toLowerCase();
    if (!isEmail(e)) throw new Error('Enter a valid email.');

    const memberDomain = emailDomain(e);

    if (team.members.includes(e)) throw new Error('Member already exists.');

    if (!team.allowAnyDomain) {
      if (!memberDomain || !team.allowedDomains.includes(memberDomain)) {
        throw new Error(
          `Only emails from allowed domains are permitted: ${team.allowedDomains.join(', ')}`
        );
      }
    }

    await updateDoc(ref, { members: arrayUnion(e) });
    return e;
  };

  const removeMember = async email => {
    await updateDoc(ref, { members: arrayRemove((email || '').toLowerCase()) });
  };

  const addAllowedDomain = async domain => {
    const cleanDomain = domain.toLowerCase().trim();
    if (!cleanDomain) throw new Error('Please enter a valid domain.');
    if (team.allowedDomains.includes(cleanDomain)) throw new Error('Domain already allowed.');

    await updateDoc(ref, {
      allowedDomains: arrayUnion(cleanDomain),
      allowAnyDomain: false,
    });
  };

  const removeAllowedDomain = async domain => {
    const cleanDomain = domain.toLowerCase().trim();
    if (cleanDomain === userDomain) throw new Error('Cannot remove your own domain.');

    await updateDoc(ref, { allowedDomains: arrayRemove(cleanDomain) });
  };

  const toggleDomainRestriction = async allowAny => {
    await updateDoc(ref, {
      allowAnyDomain: allowAny,
      allowedDomains: allowAny ? [] : [userDomain],
    });
  };

  return {
    team,
    addMember,
    removeMember,
    addAllowedDomain,
    removeAllowedDomain,
    toggleDomainRestriction,
    loading,
    error,
    orgId,
    userDomain,
  };
}
